/* CancelAppointmentDialog — patient-side cancellation with reason picker.
 *
 * Mirrors the doctor-side CancelCaseDialog pattern but pared down for an
 * appointment (not a whole case):
 *   - No type-the-code confirmation gate — cancelling an appointment is
 *     reversible (patient just books another), so the extra friction
 *     isn't worth it.
 *   - 6 structured reasons + Other (so admin can analyse cancellation
 *     patterns by reason — "70% of patient cancels are scheduling
 *     conflicts" is actionable; a free-form blob isn't).
 *   - Optional notes (becomes required when "Other" is picked).
 *
 * Backend contract: PUT /api/appointments/{id}/status?status=cancelled
 * (legacy endpoint). The reason + notes are composed into a single
 * `cancellation_reason` query param the backend records on the
 * appointment doc.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Warning, XCircle, Calendar, Clock } from "@phosphor-icons/react";

const REASONS = [
    { value: "scheduling_conflict", label: "Scheduling conflict",        hint: "Work, travel, or another commitment came up" },
    { value: "feeling_unwell",       label: "Feeling unwell",             hint: "Not well enough to come in" },
    { value: "personal_emergency",   label: "Personal / family emergency",hint: "Something urgent at home" },
    { value: "no_longer_needed",     label: "I no longer need this visit",hint: "Concern resolved or treatment plan changed" },
    { value: "transport_issue",      label: "Transport / commute issue",  hint: "Can't reach the clinic in time" },
    { value: "rescheduling_soon",    label: "I'll book a new slot soon",  hint: "Cancel now; reschedule on my own later" },
    { value: "other",                label: "Other",                      hint: "None of the above — tell us in the notes" },
];

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
            weekday: "short", day: "2-digit", month: "short", year: "numeric",
        });
    } catch { return iso; }
};

const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h)) return t;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
};

export default function CancelAppointmentDialog({ open, onOpenChange, appt, onCancelled }) {
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setReason("");
            setNotes("");
            setSubmitting(false);
        }
    }, [open]);

    if (!appt) return null;

    const otherSelected = reason === "other";
    const notesTrimmed = notes.trim();
    // Submit gate: pick a reason. "Other" forces non-empty notes — Pydantic
    // would silently accept an empty string otherwise.
    const canSubmit = !!reason && (!otherSelected || notesTrimmed.length >= 3);

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            // Compose the human-readable reason for audit. Backend stores
            // this verbatim on appointment.cancellation_reason; admin
            // analytics can split on " — " to get the structured key.
            const reasonLabel = REASONS.find((r) => r.value === reason)?.label || reason;
            const composed = notesTrimmed
                ? `${reasonLabel} — ${notesTrimmed}`
                : reasonLabel;
            // Existing endpoint takes the reason as a query param.
            await api.put(
                `/appointments/${appt.id}/status?status=cancelled&cancellation_reason=${encodeURIComponent(composed)}`,
            );
            toast.success("Appointment cancelled");
            onOpenChange(false);
            onCancelled?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not cancel the appointment");
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !submitting && onOpenChange(false)}>
            <DialogContent className="max-w-lg" data-testid="cancel-appt-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-lg font-bold flex items-center gap-2">
                        <XCircle size={20} weight="duotone" className="text-rose-600" />
                        Cancel this appointment?
                    </DialogTitle>
                    <DialogDescription>
                        A short reason helps the clinic know if they need to follow up — and helps us improve the experience.
                    </DialogDescription>
                </DialogHeader>

                {/* Appointment summary — confirms what's about to be cancelled */}
                <div className="rounded-xl bg-secondary/40 border border-border px-3.5 py-3" data-testid="cancel-appt-summary">
                    <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                            <Calendar size={16} weight="duotone" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-sm">{fmtDate(appt.date)} <span className="text-muted-foreground font-normal">·</span> {fmtTime(appt.time)}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2.5 gap-y-0.5">
                                <span>{appt.doctor_name || "Doctor"}</span>
                                {appt.clinic_name && <><span>·</span><span>{appt.clinic_name}</span></>}
                                <span>·</span>
                                <span className="capitalize"><Clock size={10} className="inline mr-0.5 -mt-0.5" />{appt.duration_minutes || 30} min</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4 max-h-[55vh] overflow-y-auto">
                    <div>
                        <Label className="text-xs font-bold tracking-wider uppercase text-muted-foreground">Reason</Label>
                        <RadioGroup value={reason} onValueChange={setReason} className="mt-2 space-y-1.5" data-testid="cancel-reason-group">
                            {REASONS.map((r) => (
                                <label
                                    key={r.value}
                                    className={`flex items-start gap-3 rounded-lg border cursor-pointer px-3 py-2.5 transition-colors ${
                                        reason === r.value ? "border-primary bg-primary/5" : "border-border hover:border-foreground/20"
                                    }`}
                                    data-testid={`cancel-reason-${r.value}`}
                                >
                                    <RadioGroupItem value={r.value} id={`cancel-${r.value}`} className="mt-1 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{r.label}</div>
                                        <div className="text-xs text-muted-foreground">{r.hint}</div>
                                    </div>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>

                    <div>
                        <Label htmlFor="cancel-notes" className="text-xs font-bold tracking-wider uppercase text-muted-foreground">
                            Notes <span className="font-normal normal-case tracking-normal text-muted-foreground/80">
                                {otherSelected ? "(required)" : "(optional)"}
                            </span>
                        </Label>
                        <Textarea
                            id="cancel-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={otherSelected ? "Tell us what happened so we can serve you better next time" : "Anything else the clinic should know"}
                            className="mt-1.5 text-sm resize-none"
                            data-testid="cancel-notes-input"
                        />
                    </div>

                    {otherSelected && notesTrimmed.length < 3 && (
                        <div className="text-xs text-amber-700 inline-flex items-center gap-1.5" data-testid="cancel-other-hint">
                            <Warning size={12} weight="fill" /> Please add a short note when picking "Other".
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="rounded-full"
                        data-testid="cancel-keep-btn"
                    >
                        Keep my appointment
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit || submitting}
                        className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
                        data-testid="cancel-confirm-btn"
                    >
                        {submitting ? "Cancelling…" : "Cancel appointment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
